import { DropdownMenu, Text } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { entityService } from "@/services/breeze/EntityService";
import { IEntity } from "@/types/breeze/Entity";
import { Field } from "@/types/breeze/Field";

interface RelationshipFieldTreeProps {
  entityName: string;
  onSelect: (path: string) => void;
  maxDepth?: number;
  pathPrefix?: string;
  depth?: number;
  visited?: Set<string>;
  fieldFilter?: (field: Field) => boolean;
  /** When true, relationship submenus include a leaf entry that selects the FK column itself. */
  allowRelationshipAsLeaf?: boolean;
}

export default function RelationshipFieldTree({
  entityName,
  onSelect,
  maxDepth = 3,
  pathPrefix = "",
  depth = 0,
  visited,
  fieldFilter,
  allowRelationshipAsLeaf = false,
}: RelationshipFieldTreeProps) {
  const { data: entity, isLoading } = useQuery({
    queryKey: ["entity", entityName, true],
    queryFn: () => entityService.getByName(entityName, true),
    staleTime: 5 * 60 * 1000,
  });

  const visitedSet = visited ?? new Set<string>([entityName]);
  const relMap = entity ? buildRelationshipMap(entity) : new Map<string, string>();

  if (isLoading || !entity) {
    return (
      <DropdownMenu.Item disabled>
        <Text size="1" color="gray">Loading {entityName}…</Text>
      </DropdownMenu.Item>
    );
  }

  const fields = (entity.fields ?? [])
    .filter((f) => !f.relationship_id || relMap.has(f.name))
    .filter((f) => (fieldFilter ? fieldFilter(f) : true));

  return (
    <>
      {fields.map((field) => (
        <FieldRow
          key={field.id ?? field.name}
          field={field}
          relatedEntityName={relMap.get(field.name)}
          pathPrefix={pathPrefix}
          depth={depth}
          maxDepth={maxDepth}
          visited={visitedSet}
          onSelect={onSelect}
          fieldFilter={fieldFilter}
          allowRelationshipAsLeaf={allowRelationshipAsLeaf}
        />
      ))}
    </>
  );
}

interface FieldRowProps {
  field: Field;
  relatedEntityName?: string;
  pathPrefix: string;
  depth: number;
  maxDepth: number;
  visited: Set<string>;
  onSelect: (path: string) => void;
  fieldFilter?: (field: Field) => boolean;
  allowRelationshipAsLeaf?: boolean;
}

function FieldRow({
  field,
  relatedEntityName,
  pathPrefix,
  depth,
  maxDepth,
  visited,
  onSelect,
  fieldFilter,
  allowRelationshipAsLeaf = false,
}: FieldRowProps) {
  const fullPath = pathPrefix ? `${pathPrefix}.${field.name}` : field.name;
  const canDrill = !!relatedEntityName && depth < maxDepth && !visited.has(relatedEntityName);

  if (canDrill) {
    const nextVisited = new Set(visited).add(relatedEntityName);
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <span>
            {field.label || field.name}
            <Text size="1" color="gray" style={{ marginLeft: 6 }}>
              → {relatedEntityName}
            </Text>
          </span>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent style={{ minWidth: 220, maxHeight: 320, zIndex: 11000 }}>
          {allowRelationshipAsLeaf && (
            <>
              <DropdownMenu.Item onClick={() => onSelect(fullPath)}>
                <Text size="1" color="gray">Insert {field.name} directly</Text>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
            </>
          )}
          <RelationshipFieldTree
            entityName={relatedEntityName!}
            onSelect={onSelect}
            maxDepth={maxDepth}
            pathPrefix={fullPath}
            depth={depth + 1}
            visited={nextVisited}
            fieldFilter={fieldFilter}
            allowRelationshipAsLeaf={allowRelationshipAsLeaf}
          />
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  }

  return (
    <DropdownMenu.Item onClick={() => onSelect(fullPath)}>
      <span>
        {field.label || field.name}
        {field.name !== (field.label ?? field.name) && (
          <Text size="1" color="gray" style={{ marginLeft: 6 }}>
            {field.name}
          </Text>
        )}
      </span>
    </DropdownMenu.Item>
  );
}

function buildRelationshipMap(entity: IEntity): Map<string, string> {
  const map = new Map<string, string>();
  for (const rel of entity.source_relationships ?? []) {
    if (rel.type !== "many-to-one" && rel.type !== "one-to-one") continue;
    if (!rel.source_field_name || !rel.target_entity_name) continue;
    map.set(rel.source_field_name, rel.target_entity_name);
  }
  return map;
}
